// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { TimelineDensity } from '@/constants/timeline-density';
import { DateTime, Interval } from 'luxon';

/**
 * Returns the largest interval that can be displayed in a timeline with the
 * provided width and density, with  the provided date time roughly 75% from
 * the start.
 *
 * @param dateTime - The date time around which to center the interval.
 * @param density - The density of the timeline.
 * @param containerWidth - The width of the timeline container.
 * @param unitWidth - The width of a single unit in the timeline.
 */
export function getTimelineInterval(
  dateTime: DateTime,
  density: TimelineDensity,
  containerWidth: number,
  unitWidth: number
) {
  // If the container width is zero, the timeline will not be visible. In this
  // case, return an empty interval so that consumers of this hook can identify
  // the "empty" state without needing to be aware of the container width.
  if (containerWidth === 0) {
    return Interval.fromDateTimes(dateTime, dateTime);
  }

  const count = Math.floor(containerWidth / unitWidth);
  const rightCount = Math.round(count * 0.25);
  const leftCount = count - rightCount;

  return Interval.fromDateTimes(
    dateTime.startOf(density).minus({ [density]: leftCount }),
    // Add one millisecond to the end of the interval to create an inclusive
    // interval. Luxon intervals are inclusive of the start but exclusive of
    // the end.
    dateTime.startOf(density).plus({ [density]: rightCount, millisecond: 1 })
  );
}
